import {
  Button,
  ClearBehaviour,
  ConfirmationBox,
  GroupBox,
  Select,
  Toolbar,
  Tooltip,
} from '@infotech-soe/erp-ui-kit';
import type {
  ConfirmationBoxProps,
  ToolbarOption,
} from '@infotech-soe/erp-ui-kit';
import { TFormFieldName, tg } from '@infotech-soe/metaschema';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  TDatasetTotalsConfigFront,
  TOTALS_FORMULA_ATTACHMENT,
  TOTALS_FORMULA_TYPE,
  TotalsRuleFront,
} from '../../../../models/user-config/DisplayModeConfig';
import { useStyles as useDialogStyles } from '../../dialogs/styles';

type PartialDatasetTotalsRule = [
  id: TotalsRuleFront[0],
  formula: TotalsRuleFront[1] | undefined,
  attachment: TotalsRuleFront[2] | undefined,
];

interface FormulasFormProps extends Pick<
  ConfirmationBoxProps,
  'open' | 'onCancel'
> {
  field: TFormFieldName;
  totals?: TDatasetTotalsConfigFront;
  onSubmit?: (
    field: TFormFieldName,
    rules?: TotalsRuleFront[],
    updateDataset?: boolean
  ) => void;
}

const AVAILABLE_FORMULAS_ARR = [
  TOTALS_FORMULA_TYPE.SUM,
  TOTALS_FORMULA_TYPE.AVG,
  TOTALS_FORMULA_TYPE.MIN,
  TOTALS_FORMULA_TYPE.MAX,
];

const ATTACHMENTS_ARR = [
  TOTALS_FORMULA_ATTACHMENT.ALL,
  TOTALS_FORMULA_ATTACHMENT.SELECTED,
];

export const formulasFormMessages = defineMessages({
  title: {
    id: 'core.category.grid.config.formulas.title',
    defaultMessage: 'Totals formulas',
  },
  [TOTALS_FORMULA_ATTACHMENT.ALL]: {
    id: 'core.category.grid.config.formulas.attachmentByAll',
    defaultMessage: 'Calculate by all records',
  },
  [TOTALS_FORMULA_ATTACHMENT.SELECTED]: {
    id: 'core.category.grid.config.formulas.attachmentBySelected',
    defaultMessage: 'Calculate by selected records',
  },
  [TOTALS_FORMULA_TYPE.SUM]: {
    id: 'core.category.grid.config.formulas.sum',
    defaultMessage: 'Sum',
  },
  [TOTALS_FORMULA_TYPE.AVG]: {
    id: 'core.category.grid.config.formulas.avg',
    defaultMessage: 'Average',
  },
  [TOTALS_FORMULA_TYPE.MIN]: {
    id: 'core.category.grid.config.formulas.min',
    defaultMessage: 'Minimum',
  },
  [TOTALS_FORMULA_TYPE.MAX]: {
    id: 'core.category.grid.config.formulas.max',
    defaultMessage: 'Maximum',
  },
});

const isTotalsRulesValid = (
  rules: PartialDatasetTotalsRule[] | undefined
): rules is TotalsRuleFront[] | undefined => {
  const uniqueMap = new Map();
  const duplicates = [];
  const emptyFields = [];

  if (!rules) {
    return true;
  }
  for (const rule of rules) {
    const [id, formula, attachment] = rule;
    if (tg.isNothing(formula) || tg.isNothing(attachment)) {
      emptyFields.push(id);
      break;
    }
    const currentUniqueKey = formula + attachment + '';
    if (uniqueMap.has(currentUniqueKey)) {
      duplicates.push(id);
    } else {
      uniqueMap.set(currentUniqueKey, id);
    }
  }

  return !duplicates.length && !emptyFields.length;
};

const isNeedToUpdate = (
  oldConfig?: TotalsRuleFront[],
  newConfig?: TotalsRuleFront[]
) =>
  newConfig?.some(
    ([id, , attachment]) =>
      attachment === TOTALS_FORMULA_ATTACHMENT.ALL &&
      oldConfig?.find(([oldId]) => oldId === id)?.[2] !==
        TOTALS_FORMULA_ATTACHMENT.ALL
  );

const emptyTotalsRule = (init = false): PartialDatasetTotalsRule => [
  window.crypto.getRandomValues(new Uint8Array(1))[0] + '' + Date.now(),
  init ? TOTALS_FORMULA_TYPE.SUM : undefined,
  init ? TOTALS_FORMULA_ATTACHMENT.ALL : undefined,
];

const FormulasForm: FC<FormulasFormProps> = ({
  open,
  field,
  totals,
  onCancel,
  onSubmit,
}) => {
  const dialogClasses = useDialogStyles();
  const { formatMessage } = useIntl();
  const [rules, setRules] = useState<PartialDatasetTotalsRule[] | undefined>(
    totals?.[field] ?? [emptyTotalsRule(true)]
  );

  // TODO: replace any with TypeDropdownListOptions[]
  const formulaTypeOptions: any[] = useMemo(
    () =>
      AVAILABLE_FORMULAS_ARR.map(formula => ({
        name: formula,
        label: formatMessage(formulasFormMessages[formula]),
        value: formula,
        type: 'option',
      })),
    [formatMessage]
  );

  // TODO: replace any with TypeDropdownListOptions[]
  const formulaAttachmentOptions: any[] = useMemo(
    () =>
      ATTACHMENTS_ARR.map(att => ({
        type: 'option',
        value: att,
        name: att,
        label: formatMessage(formulasFormMessages[att]),
      })),
    [formatMessage]
  );

  const toolbarHandler = useCallback((name: 'add' | 'deleteAll') => {
    if (name === 'add') {
      setRules(oldRules => [...(oldRules ?? []), emptyTotalsRule()]);
    } else if (name === 'deleteAll') {
      setRules(undefined);
    }
  }, []);

  const handleFormulaAttachmentChange = useCallback(
    (key: string) => (formulaAttachment: TOTALS_FORMULA_ATTACHMENT | null) => {
      setRules(
        rules?.map(([id, formula, attachment]) => [
          id,
          formula,
          id === key ? (formulaAttachment ?? undefined) : attachment,
        ])
      );
    },
    [rules]
  );

  const handleRuleTypeChange = useCallback(
    (key: string) => (formulaType: TOTALS_FORMULA_TYPE | null) => {
      setRules(
        rules?.map(([id, formula, attachment]) => [
          id,
          id === key ? (formulaType ?? undefined) : formula,
          attachment,
        ])
      );
    },
    [rules]
  );

  const handleRuleRemove = useCallback(
    (key: string) => () => {
      setRules(oldRules => oldRules?.filter(([id]) => id !== key));
    },
    []
  );

  const handleSubmitForm = useCallback(() => {
    if (isTotalsRulesValid(rules) && field && onSubmit) {
      onSubmit(field, rules, isNeedToUpdate(totals?.[field], rules));
    }
  }, [field, onSubmit, rules, totals]);

  const toolbarOptions: Array<ToolbarOption<'add' | 'deleteAll'>> = useMemo(
    () => [
      {
        type: 'button',
        icon: 'add',
        name: 'add',
        label: formatMessage({
          id: 'core.category.grid.config.rules.condition.add',
        }),
        tabIndex: 0,
      },
      {
        type: 'button',
        icon: 'delete',
        name: 'deleteAll',
        label: formatMessage({
          id: 'core.category.grid.config.formulas.deleteAll',
        }),
        tabIndex: 0,
      },
    ],
    [formatMessage]
  );

  const ToolbarHeader = useMemo(
    () => <Toolbar options={toolbarOptions} onSelect={toolbarHandler} />,
    [toolbarOptions, toolbarHandler]
  );

  return (
    <ConfirmationBox
      className={dialogClasses.dialog}
      header={ToolbarHeader}
      height="auto"
      open={open}
      submitForbidden={!isTotalsRulesValid(rules)}
      title={formatMessage(formulasFormMessages.title)}
      onCancel={onCancel}
      onClose={onCancel}
      onConfirm={handleSubmitForm}
    >
      {rules?.map(([key, formula, attachment]) => (
        <GroupBox key={key}>
          <GroupBox.Content className="row no-wrap">
            <Select
              required
              value={formula ?? null}
              options={formulaTypeOptions}
              withClear={ClearBehaviour.NONE}
              onChange={handleRuleTypeChange(key)}
              onSubmit={handleSubmitForm}
            />
            <Select
              className="m-ml-sm"
              required
              value={attachment ?? null}
              options={formulaAttachmentOptions}
              withClear={ClearBehaviour.NONE}
              onChange={handleFormulaAttachmentChange(key)}
              onSubmit={handleSubmitForm}
            />
            <div>
              <Tooltip
                label={formatMessage({
                  id: 'core.category.grid.config.rules.condition.delete',
                })}
              >
                <Button
                  className="col-auto"
                  focusable
                  icon="delete"
                  name="formula"
                  variant="borderless"
                  onClick={handleRuleRemove(key)}
                />
              </Tooltip>
            </div>
          </GroupBox.Content>
        </GroupBox>
      ))}
    </ConfirmationBox>
  );
};

export default FormulasForm;
